import { blotterPreferenceStatePersistenceApiConnector } from 'apiConnector/blotters';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { EMPTY, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import type { SpEpic } from 'shared/middlewares/epic/epicMiddleware.factory';
import {
  quoteBlotterPreferenceStateGet,
  quoteBlotterPreferenceStateLoaded,
  quoteBlotterPreferenceStateLoadingFailed,
  quoteBlotterPreferenceStatePersisted,
  quoteBlotterPreferenceStatePersistenceFailed,
} from 'state/blotters/quote';
import type { EpicWithThunk } from 'state/state.common';

import { toBlotterPreferences } from '../../shared/preferenceState/preferenceStateEpics.helper';

export function persistPreferenceStateEpic(
  aggregationFrequency: number = 1000,
  scheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    action$,
    state$,
    {
      httpClient,
      selectors: {
        fromQuoteBlotter: { canSaveBlotterPreferences },
      },
    },
  ) =>
    action$.pipe(
      ofType('QUOTE_BLOTTER_PREFERENCE_STATE_PERSISTED'),
      withLatestFrom(state$),
      filter(([, state]) => canSaveBlotterPreferences(state)),
      debounceTime(aggregationFrequency, scheduler),
      mergeMap(([action]) => {
        return blotterPreferenceStatePersistenceApiConnector(httpClient)
          .saveState(action.blotterPreferenceState, 'quotes')
          .pipe(
            mergeMap(_ => EMPTY),
            catchError(() => {
              return of(quoteBlotterPreferenceStatePersistenceFailed());
            }),
          );
      }),
    );
}

export function loadPreferenceStateEpic(): EpicWithThunk {
  return (actions$, _state$, { httpClient }) => {
    return actions$.pipe(
      ofType('QUOTE_BLOTTER_PREFERENCE_STATE_GET'),
      mergeMap(() => {
        return blotterPreferenceStatePersistenceApiConnector(httpClient)
          .getState('quotes')
          .pipe(
            map(response => quoteBlotterPreferenceStateLoaded(toBlotterPreferences(response))),
            catchError(() => {
              return of(quoteBlotterPreferenceStateLoadingFailed(toBlotterPreferences(undefined)));
            }),
          );
      }),
    );
  };
}

// TODO catch Error in Epic
export function columnStateChangedEpic(): EpicWithThunk {
  return (action$, state$, { selectors: { getQuoteBlotterPreferenceState } }) =>
    action$.pipe(
      ofType('QUOTE_BLOTTER_COLUMN_STATE_CHANGED'),
      withLatestFrom(state$),
      map(([, state]) => {
        const blotterPreferenceState = getQuoteBlotterPreferenceState(state);
        return quoteBlotterPreferenceStatePersisted(blotterPreferenceState);
      }),
      catchError(_ => EMPTY),
    );
}

// TODO catch Error in Epic
export function pageSizeChangedEpic(): EpicWithThunk {
  return (action$, state$, { selectors: { getQuoteBlotterPreferenceState } }) =>
    action$.pipe(
      ofType('QUOTE_BLOTTER_ITEMS_PER_PAGE_CHANGED'),
      withLatestFrom(state$),
      map(([, state]) => {
        const blotterPreferenceState = getQuoteBlotterPreferenceState(state);
        return quoteBlotterPreferenceStatePersisted(blotterPreferenceState);
      }),
      catchError(_ => EMPTY),
    );
}

export function fetchColumnDefinitionReceivedEpic(): SpEpic {
  return action$ =>
    action$.pipe(
      ofType('QUOTE_BLOTTER_COLUMN_DEFINITION_RECEIVED'),
      map(quoteBlotterPreferenceStateGet),
      catchError(_ => EMPTY),
    );
}
